import React, { FC } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

import { PlayerPoints, TournamentConfig } from '../../../../database.schema';
import { isLoggedIn } from '../../../app/authentication/auth';
import { CentreHeading, FlexRowSpaceBetween, SectionWrapper } from '../../common-styles/common-styles';
import { useFetchTeamTotalsByDay, TotalsPointsWithMatchResults } from '../../../api/hooks/use-fetch-team-totals-by-day';
import { MatchResult } from './match-result';

const PageWrapper = styled.div`
  margin-right: 2rem;
  display: flex;
  justify-content: space-evenly;
  flex-direction: column;
  &h3: {
    margin: 0;
  }
`;
const Title = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 0.5rem 0.5rem 0.25rem 0;
  div {
    h3 {
      font-size: 20px;
    }
  }
`;
const TeamPoints = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 0 0 0.5rem;
`;
const Button = styled.button`
  border-radius: 5px;
  margin-left: 1rem;
  background: lightblue;
  border: 1px solid #5c9db2;
  font-weight: 500;
  color: white;
  height: 2rem;
`;

const ResultListWrapper = styled.div`
  border-bottom: 4px dashed #5c9db2;
  padding: 10px 0 20px;
  margin: 10px 0;
`;

const LeftTotal = styled.div`
  width: 50%;
  border-top-left-radius: 0.25rem;
  border-bottom-left-radius: 0.25rem;
  background-color: red;
  height: 1.75rem;
  padding: 0.25rem 1rem;
  text-align: right;
  color: white;
`;

const RightTotal = styled.div`
  width: 50%;
  border-top-right-radius: 0.25rem;
  border-bottom-right-radius: 0.25rem;
  background-color: blue;
  height: 1.75rem;
  padding: 0.25rem 1rem;
  text-align: left;
  color: white;
`;

const EditButton = styled(Button)`
  margin-top: 1rem;
  padding: 0 0.5rem;
`;

const PointsTD = styled.td`
  text-align: right;
  padding-right: 4rem;
`;
const NearestThePinTD = styled.td`
  font-size: 12px;
  text-align: center;
`;
type ResultAsListProps = {
  tournamentIndex: number;
  config: TournamentConfig;
};

export const ResultsAsList: FC<ResultAsListProps> = ({ tournamentIndex, config }): JSX.Element | null => {
  const navigate = useNavigate();
  const { isLoading, error, data } = useFetchTeamTotalsByDay(tournamentIndex);
  const teamTotals = data as Array<TotalsPointsWithMatchResults>;

  if (isLoading) {
    return <div>Loading...</div>;
  }
  if (error) {
    return <div>Error...</div>;
  }
  if (teamTotals.length === 0) {
    return null;
  }

  return (
    <SectionWrapper data-testid="results">
      <CentreHeading>
        <h2>Results</h2>
      </CentreHeading>
      {teamTotals.map((teamTotal: TotalsPointsWithMatchResults, dayIndex: number) => (
        <ResultListWrapper key={`tour-${tournamentIndex}-result-${dayIndex}`}>
          <Title>
            <div>
              <h3>{`Day: ${dayIndex + 1}`}</h3>
            </div>
            <div>
              <h3>Course: {teamTotal.courseName}</h3>
            </div>
          </Title>
          <Title>
            <h4>{`Format: ${teamTotal.format.toLocaleLowerCase() === 'pairs' ? 'Four-Ball' : 'Singles'}`}</h4>
          </Title>
          <div>
            {teamTotal.matchResults.map((match, index) => (
              <MatchResult
                key={`match-${dayIndex}-${index}`}
                match={match}
                matchIndex={index}
                dayIndex={dayIndex}
                startingHole={teamTotal.startingHole}
                tourIndex={tournamentIndex}
                format={teamTotal.format as 'Pairs' | 'Singles'}
              />
            ))}
          </div>
          <h3>Points</h3>
          <TeamPoints>
            <LeftTotal>{teamTotal.totals.team1Points}</LeftTotal>
            <RightTotal>{teamTotal.totals.team2Points}</RightTotal>
          </TeamPoints>
          <h3>Overall Points</h3>
          <TeamPoints>
            <LeftTotal>{teamTotal.teamRunningTotalPoints.team1Points}</LeftTotal>
            <RightTotal>{teamTotal.teamRunningTotalPoints.team2Points}</RightTotal>
          </TeamPoints>
          <PageWrapper>
            <div>
              <h2>Individual</h2>
            </div>
            <table>
              <tbody>
                <tr>
                  <th></th>
                  <th></th>
                  <th></th>
                  <th>Stableford</th>
                  {config.showCaptainsRace && <th>Captains</th>}
                  {config.showLongestDrive && <th>LD</th>}
                  {config.showNearestThePin && <th>NP</th>}
                  {config.showNearestThePinIn2 && <th>NPIn2 </th>}
                </tr>
                {teamTotal.totals.individual?.map((player: PlayerPoints) => (
                  <tr key={player.player.nickName}>
                    <td style={{ minWidth: '2rem' }}></td>
                    <td>{player.position}</td>
                    <td>{player.player.nickName}</td>
                    <PointsTD>{player.points}</PointsTD>
                    {config.showCaptainsRace && <PointsTD>{player.captainPoints}</PointsTD>}
                    {config.showLongestDrive && player.longestDrive ? <NearestThePinTD data-testid={`ld-${player.player.nickName}`}>&#128163;</NearestThePinTD> : <td></td>}
                    {config.showNearestThePin && player.nearestThePin ? <NearestThePinTD data-testid={`ntp-${player.player.nickName}`}>&#9971;</NearestThePinTD> : <td></td>}
                    {config.showNearestThePinIn2 && player.nearestThePinIn2 ? (
                      <NearestThePinTD data-testid={`ntpIn2-${player.player.nickName}`}>&#9971;</NearestThePinTD>
                    ) : (
                      <td></td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </PageWrapper>
          {isLoggedIn() && !teamTotals[0].isArchivedTournament && (
            <FlexRowSpaceBetween>
              <EditButton onClick={() => navigate(`/edit-results/${tournamentIndex}/${dayIndex}`)}>Edit Results</EditButton>
            </FlexRowSpaceBetween>
          )}
        </ResultListWrapper>
      ))}
    </SectionWrapper>
  );
};
